/**
 * Test Download Utilities
 *
 * Functions for downloading and copying generated tests.
 */

import { encryptTestCode } from "./encryption";
import type { GeneratedTest } from "./types";
import { getStateIdFromUrl } from "./utils";

// ============================================================================
// Download Functions
// ============================================================================

/**
 * Download a generated test as an encrypted .enc.txt file
 * Uses stateId from URL as filename for correlation with kiosk session
 * The file must be decrypted using decode-test.ts before running
 */
export function downloadGeneratedTest(test: GeneratedTest): void {
  const encrypted = encryptTestCode(test.testCode);
  const blob = new Blob([encrypted], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  // Use stateId from URL as filename (correlates with localStorage kiosk-state:{stateId})
  const stateId = getStateIdFromUrl();
  const filename = stateId
    ? `${stateId}.enc.txt`
    : `trace-${test.metadata.traceId.replace(/[^a-z0-9-]/gi, "-")}.enc.txt`;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Create a blob URL for the generated test that can be clicked to download
 * Returns a URL that expires when the page is closed
 */
export function createTestDownloadUrl(test: GeneratedTest): string {
  const blob = new Blob([test.testCode], { type: "text/typescript" });
  return URL.createObjectURL(blob);
}

// ============================================================================
// Clipboard Functions
// ============================================================================

/**
 * Copy encrypted test code to clipboard
 */
export async function copyTestToClipboard(
  test: GeneratedTest,
): Promise<boolean> {
  try {
    const encrypted = encryptTestCode(test.testCode);
    await navigator.clipboard.writeText(encrypted);
    return true;
  } catch (error) {
    console.error("[playwright-generator] Failed to copy to clipboard:", error);
    return false;
  }
}
